import { Text } from '@lmn/design-system';
import styled, { keyframes } from 'styled-components';

const taxesTooltipSlideIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.3);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

/* Outer wrapper: column, right-aligned */
export const TaxesIncludedTooltipWrapper = styled.div`
  animation: ${taxesTooltipSlideIn} 0.5s ease-in-out;
  transform-origin: top right;
  position: absolute;
  top: 100%;
  right: -26px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

/* Caret pointing upward at the info icon at the right edge of the taxes label.
   Rendered as an inline SVG so we can stroke ONLY the two slanted edges and
   leave the base un-stroked — the open base merges seamlessly with the card
   below (margin-bottom: -1px lets the path's filled base cover the 1px chunk
   of the card's top border directly underneath the caret).
   `overflow: visible` keeps the half-pixel stroke at the path endpoints from
   being clipped by the SVG viewport. */
export const TaxesIncludedTooltipCaret = styled.svg`
  position: relative;
  z-index: 1;
  margin-right: 16px;
  margin-bottom: -1px;
  display: block;
  width: 32px;
  height: 14px;
  overflow: visible;

  path {
    fill: ${({ theme }) => theme.kallisto.color.background.static.backgroundLayer02};
    stroke: ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
    stroke-width: ${({ theme }) => theme.kallisto.border.width.width12};
  }
`;

/* Light content bubble */
export const TaxesIncludedTooltipContent = styled.div`
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundLayer02};
  border: ${({ theme }) => theme.kallisto.border.width.width12} solid
    ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
  border-radius: ${({ theme }) => theme.kallisto.border.radius.radius200};
  box-shadow: ${({ theme }) => theme.kallisto.shadow.largeDownShadow};
  padding: ${({ theme }) => theme.kallisto.space.space100};
  width: 100%;
  position: relative;
`;

/* Row inside content: deposit icon + text, with right padding for close button */
export const TaxesIncludedTooltipInner = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.kallisto.space.space200};
  padding: ${({ theme }) => theme.kallisto.space.space200} 56px ${({ theme }) => theme.kallisto.space.space200}
    ${({ theme }) => theme.kallisto.space.space200};
`;

export const TaxesIncludedTooltipImage = styled.img`
  width: 56px;
  height: 56px;
  flex-shrink: 0;
`;

export const TaxesIncludedTooltipTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.kallisto.space.space50};
`;

export const TaxesIncludedTooltipText = styled(Text).attrs({ variant: 'Body' })<{ $weight?: 'bold' | 'regular' }>`
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  white-space: nowrap;
  font-weight: ${({ theme, $weight = 'regular' }) =>
    $weight === 'bold' ? theme.kallisto.font.weight.bold : theme.kallisto.font.weight.regular};
`;

/* Absolutely positioned close button at top-right corner of the content bubble */
export const TaxesIncludedTooltipCloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  padding: 0;
`;
